import { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { useIntl } from 'react-intl'
import TabsComponent from '../../stories/atoms/tabs'
import { tabsActivationModeOptions } from '../../stories/atoms/tabs/constants'
import SearchComponent from '../../stories/molecules/search'
import { SpinnerGrey } from '../../stories/atoms/icons/special'
import { iconSizeOptions } from '../../stories/atoms/icons/icons-sizes'

import {
  CopyMembersTabsContent,
  CopyMembersTabsSearchContainer,
  CopyModalContainer,
  CopyModalTabsContent,
  CopyProjectTabTitle
} from '../styled-components'
import MembersList from './members/members-list'
import { searchVariantsOptions } from '../../stories/molecules/search/constants'
import { Search } from '../../stories/atoms/icons/core'
import colorsOptions from '../../stories/fundamentals/colors-styles/constants'
import ContactsList from './contacts/contacts-list'
import TeamsList from './team/teams-list'
import { copyProjectMembersTabOptions } from '../constants'
import { useDebounce } from '../../../utils/customHooks'

const SEARCH_DEBOUNCE_DELAY = 300

const normalizeSearchTerm = (value = '') => value.trim().toLowerCase()

const includesSearchTerm = (searchTerm, values = []) => {
  if (!searchTerm) {
    return true
  }

  return values.some((value) =>
    `${value ?? ''}`.toLowerCase().includes(searchTerm)
  )
}

const filterMembersBySearch = (members = [], searchTerm = '') => {
  return members.filter((item) =>
    includesSearchTerm(searchTerm, [
      item.firstName,
      item.lastName,
      `${item.firstName ?? ''} ${item.lastName ?? ''}`,
      item.email
    ])
  )
}

const filterTeamsBySearch = (teams = [], searchTerm = '') => {
  return teams.filter((item) => includesSearchTerm(searchTerm, [item.name]))
}

const filterContactsBySearch = (contacts = [], searchTerm = '') => {
  return contacts.filter((item) =>
    includesSearchTerm(searchTerm, [
      item.displayName,
      item.displayCompanyName,
      item.firstName,
      item.lastName,
      `${item.firstName ?? ''} ${item.lastName ?? ''}`,
      item.email
    ])
  )
}

const CopyProjectMembersSelector = ({
  defaultTab = copyProjectMembersTabOptions.MEMBERS,
  members = [],
  teams = [],
  contacts = [],
  selectedItemIdsByTab = {},
  onToggleSelectItem = () => {},
  isLoadingMembers = false,
  isLoadingTeams = false,
  isLoadingContacts = false
}) => {
  const { formatMessage } = useIntl()
  const [activeTab, setActiveTab] = useState(defaultTab)
  const [searchValue, setSearchValue] = useState('')
  const debouncedSearchValue = useDebounce(searchValue, SEARCH_DEBOUNCE_DELAY)
  const normalizedSearchTerm = normalizeSearchTerm(debouncedSearchValue)

  useEffect(() => {
    setActiveTab(defaultTab)
  }, [defaultTab])

  const rawTabDataByValue = useMemo(
    () => ({
      [copyProjectMembersTabOptions.MEMBERS]: {
        items: Array.isArray(members) ? members : [],
        isLoading: isLoadingMembers
      },
      [copyProjectMembersTabOptions.TEAMS]: {
        items: Array.isArray(teams) ? teams : [],
        isLoading: isLoadingTeams
      },
      [copyProjectMembersTabOptions.CONTACTS]: {
        items: Array.isArray(contacts) ? contacts : [],
        isLoading: isLoadingContacts
      }
    }),
    [
      contacts,
      isLoadingContacts,
      isLoadingMembers,
      isLoadingTeams,
      members,
      teams
    ]
  )

  const filteredTabDataByValue = useMemo(
    () => ({
      [copyProjectMembersTabOptions.MEMBERS]: {
        items: filterMembersBySearch(
          rawTabDataByValue[copyProjectMembersTabOptions.MEMBERS].items,
          normalizedSearchTerm
        ),
        isLoading: isLoadingMembers
      },
      [copyProjectMembersTabOptions.TEAMS]: {
        items: filterTeamsBySearch(
          rawTabDataByValue[copyProjectMembersTabOptions.TEAMS].items,
          normalizedSearchTerm
        ),
        isLoading: isLoadingTeams
      },
      [copyProjectMembersTabOptions.CONTACTS]: {
        items: filterContactsBySearch(
          rawTabDataByValue[copyProjectMembersTabOptions.CONTACTS].items,
          normalizedSearchTerm
        ),
        isLoading: isLoadingContacts
      }
    }),
    [
      isLoadingContacts,
      isLoadingMembers,
      isLoadingTeams,
      normalizedSearchTerm,
      rawTabDataByValue
    ]
  )

  const currentTabData =
    rawTabDataByValue[activeTab] ??
    rawTabDataByValue[copyProjectMembersTabOptions.MEMBERS]

  const shouldShowSearch =
    currentTabData?.isLoading || currentTabData?.items.length > 0

  const getSearchValueForTab = useCallback(
    (tab) => {
      const tabData = rawTabDataByValue[tab]
      const canSearchInTab = tabData?.isLoading || tabData?.items.length > 0

      return canSearchInTab ? debouncedSearchValue : ''
    },
    [debouncedSearchValue, rawTabDataByValue]
  )

  const handleToggleMemberItem = useCallback(
    (itemId) =>
      onToggleSelectItem(copyProjectMembersTabOptions.MEMBERS, itemId),
    [onToggleSelectItem]
  )

  const handleToggleTeamItem = useCallback(
    (itemId) => onToggleSelectItem(copyProjectMembersTabOptions.TEAMS, itemId),
    [onToggleSelectItem]
  )

  const handleToggleContactItem = useCallback(
    (itemId) =>
      onToggleSelectItem(copyProjectMembersTabOptions.CONTACTS, itemId),
    [onToggleSelectItem]
  )

  return (
    <CopyModalContainer>
      <CopyModalTabsContent>
        <TabsComponent.Root
          value={activeTab}
          onValueChange={setActiveTab}
          activationMode={tabsActivationModeOptions.AUTOMATIC}
        >
          <TabsComponent.List>
            <TabsComponent.Trigger value={copyProjectMembersTabOptions.MEMBERS}>
              <CopyProjectTabTitle>
                {formatMessage({ id: 'members' })}{' '}
                {isLoadingMembers ? (
                  <SpinnerGrey size={iconSizeOptions['ICON-S']} />
                ) : (
                  `(${filteredTabDataByValue[copyProjectMembersTabOptions.MEMBERS].items.length})`
                )}
              </CopyProjectTabTitle>
            </TabsComponent.Trigger>
            <TabsComponent.Trigger value={copyProjectMembersTabOptions.TEAMS}>
              <CopyProjectTabTitle>
                {formatMessage({ id: 'teams_titleTeamGroups' })}{' '}
                {isLoadingTeams ? (
                  <SpinnerGrey size={iconSizeOptions['ICON-S']} />
                ) : (
                  `(${filteredTabDataByValue[copyProjectMembersTabOptions.TEAMS].items.length})`
                )}
              </CopyProjectTabTitle>
            </TabsComponent.Trigger>
            <TabsComponent.Trigger
              value={copyProjectMembersTabOptions.CONTACTS}
            >
              <CopyProjectTabTitle>
                {formatMessage({ id: 'personas_contacts' })}{' '}
                {isLoadingContacts ? (
                  <SpinnerGrey size={iconSizeOptions['ICON-S']} />
                ) : (
                  `(${filteredTabDataByValue[copyProjectMembersTabOptions.CONTACTS].items.length})`
                )}
              </CopyProjectTabTitle>
            </TabsComponent.Trigger>
          </TabsComponent.List>
          <CopyMembersTabsContent>
            {shouldShowSearch && (
              <CopyMembersTabsSearchContainer>
                <SearchComponent.Root
                  variant={searchVariantsOptions.DESKTOP}
                  searchValue={searchValue}
                  setSearchValue={setSearchValue}
                  defaultDesktopVariantOpen
                >
                  <SearchComponent.LeadingIcon
                    icon={Search}
                    stroke={colorsOptions.greys['grey-100']}
                    style={{
                      marginLeft: '8px'
                    }}
                  />
                  <SearchComponent.Container>
                    <SearchComponent.Input
                      placeholder={formatMessage({ id: 'search' })}
                    />
                    <SearchComponent.ClearButton />
                  </SearchComponent.Container>
                  <SearchComponent.CancelButton />
                </SearchComponent.Root>
              </CopyMembersTabsSearchContainer>
            )}
            <TabsComponent.Content
              value={copyProjectMembersTabOptions.MEMBERS}
              style={{
                padding: 0
              }}
            >
              <MembersList
                members={
                  filteredTabDataByValue[copyProjectMembersTabOptions.MEMBERS]
                    .items
                }
                isLoading={isLoadingMembers}
                searchValue={getSearchValueForTab(
                  copyProjectMembersTabOptions.MEMBERS
                )}
                selectedItemIds={
                  selectedItemIdsByTab[copyProjectMembersTabOptions.MEMBERS]
                }
                onToggleSelect={handleToggleMemberItem}
              />
            </TabsComponent.Content>
            <TabsComponent.Content
              value={copyProjectMembersTabOptions.TEAMS}
              style={{
                padding: 0
              }}
            >
              <TeamsList
                teams={
                  filteredTabDataByValue[copyProjectMembersTabOptions.TEAMS]
                    .items
                }
                isLoading={isLoadingTeams}
                searchValue={getSearchValueForTab(
                  copyProjectMembersTabOptions.TEAMS
                )}
                selectedItemIds={
                  selectedItemIdsByTab[copyProjectMembersTabOptions.TEAMS]
                }
                onToggleSelect={handleToggleTeamItem}
              />
            </TabsComponent.Content>
            <TabsComponent.Content
              value={copyProjectMembersTabOptions.CONTACTS}
              style={{
                padding: 0
              }}
            >
              <ContactsList
                contacts={
                  filteredTabDataByValue[copyProjectMembersTabOptions.CONTACTS]
                    .items
                }
                isLoading={isLoadingContacts}
                searchValue={getSearchValueForTab(
                  copyProjectMembersTabOptions.CONTACTS
                )}
                selectedItemIds={
                  selectedItemIdsByTab[copyProjectMembersTabOptions.CONTACTS]
                }
                onToggleSelect={handleToggleContactItem}
              />
            </TabsComponent.Content>
          </CopyMembersTabsContent>
        </TabsComponent.Root>
      </CopyModalTabsContent>
    </CopyModalContainer>
  )
}

CopyProjectMembersSelector.propTypes = {
  defaultTab: PropTypes.string,
  members: PropTypes.array,
  teams: PropTypes.array,
  contacts: PropTypes.array,
  selectedItemIdsByTab: PropTypes.object,
  onToggleSelectItem: PropTypes.func,
  isLoadingMembers: PropTypes.bool,
  isLoadingTeams: PropTypes.bool,
  isLoadingContacts: PropTypes.bool
}

export default CopyProjectMembersSelector
